// Ficha del activo en el panel lateral del tablero (Mantenimiento M1): ficha, estado y aptitud (de a uno, con motivo al
// liberar), documentos (bucket privado, URL firmada de 120 s), historial (solo lectura), inspecciones del activo y QR.
// Ergonomía: guardado visible, color con texto, confirmación antes de archivar o borrar, sin pedir dos veces lo que ya
// existe (sede por defecto, catálogos de trabajadores, usuarios y EPP).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Etiqueta, GuardadoVisible, type EstadoGuardado } from '../../components/tablero/Tablero';
import { fechaEc, isoLocal } from '../../lib/inspecciones/insp-logica';
import { ETIQUETA_ESTADO_INSPECCION, ETIQUETA_FRECUENCIA, FRECUENCIAS, type EstadoInspeccion } from '../../lib/inspecciones/insp-tipos';
import {
  APTITUDES, CATEGORIAS, COLOR_APTITUD, COLOR_ESTADO, CRITICIDADES, ESTADOS, ETIQUETA_APTITUD, ETIQUETA_CAMPO, ETIQUETA_CRITICIDAD,
  ETIQUETA_DOCUMENTO, ETIQUETA_ESTADO, ETIQUETA_EVENTO, ETIQUETA_PERMISO, LISTA_CATEGORIAS, PELIGROS_SUGERIDOS, TIPOS_DOCUMENTO,
  TIPOS_ENERGIA, TIPOS_PERMISO,
  type Aptitud, type Categoria, type EstadoOperativo, type FilaActivo, type FilaDocumento, type FilaHistorial, type PlazosExtintor, type TipoDocumento,
} from '../../lib/mantenimiento/mant-tipos';
import {
  avisoCambio, bloqueoRecarga, codigoRepetido, ETIQUETA_VENCIMIENTO, NOTA_VENCIMIENTOS, resultadoCambio, siguienteCodigo, validarCambio, vencimientos,
} from '../../lib/mantenimiento/mant-logica';
import {
  abrirArchivoActivo, actualizarActivo, borrarActivo, borrarDocumento, cambiarEstado, cargarDocumentos, cargarHistorial,
  cargarInspeccionesActivo, cargarPlantillasActivo, crearActivo, programarInspeccionActivo, subirDocumento, subirFoto, urlFirmada,
  validarArchivo, type CambiosActivo, type InspeccionActivo, type Opcion, type PlantillaMin,
} from '../../lib/mantenimiento/mant-datos';
import { enlaceActivo, htmlEtiqueta, qrDataUrl } from '../../lib/mantenimiento/mant-qr';

type Pestana = 'ficha' | 'estado' | 'documentos' | 'historial' | 'inspecciones' | 'qr';
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const colorVence = { vencido: '#ef4444', por_vencer: '#eab308', vigente: '#22c55e' } as const;

export interface SedeOpcion { id: string; name: string; is_main: boolean; active: boolean }

interface Props {
  activo: FilaActivo | null;
  companyId: string;
  empresa: string;
  sedes: SedeOpcion[];
  varias: boolean;
  opciones: { trabajadores: Opcion[]; usuarios: Opcion[]; epp: Opcion[]; sectores: Opcion[] };
  codigos: string[];
  puedeEditar: boolean;
  esAdmin: boolean;
  usuarioId: string | null;
  hoy: Date;
  /** Plazos de los extintores de la empresa (los de la norma si no guardó los suyos). */
  plazos: PlazosExtintor;
  onGuardado: (a: FilaActivo) => void;
  onBorrado: (id: string) => void;
}

const vacio = (sede: string | null): CambiosActivo => ({
  name: '', category: 'maquina', code: '', manufacturer: null, brand: null, model: null, serial_number: null, manufacture_year: null,
  description: null, site_id: sede, area: null, location_detail: null, tech: {}, acquired_on: null, installed_on: null, useful_life_years: null,
  manual_ref: null, responsible_worker_id: null, responsible_user_id: null, external_provider: null, hazards: [], required_epp_ids: [],
  isolation_points: [], permit_type: null, procedure_ref: null, operating_restrictions: null, criticality: 'media',
  operational_status: 'operativo', safety_status: 'apto', emergency_sector_id: null,
});

function Campo({ etiqueta, children, nota, ancho }: { etiqueta: string; children: React.ReactNode; nota?: string; ancho?: boolean }) {
  return (
    <label className={`pe2-campo${ancho ? ' mant-ancho' : ''}`}>
      <span>{etiqueta}</span>
      {children}
      {nota && <small>{nota}</small>}
    </label>
  );
}

export default function FichaActivo(p: Props) {
  const nuevo = !p.activo;
  const [pestana, setPestana] = useState<Pestana>('ficha');
  useEffect(() => { setPestana('ficha'); }, [p.activo?.id]);
  const pestanas: [Pestana, string][] = nuevo ? [['ficha', 'Ficha']]
    : [['ficha', 'Ficha'], ['estado', 'Estado y aptitud'], ['documentos', 'Documentos'], ['historial', 'Historial'], ['inspecciones', 'Inspecciones'], ['qr', 'QR']];
  return (
    <div className="pe2 mant-ficha">
      {p.activo && (
        <div className="mant-estado-linea">
          <Etiqueta texto={ETIQUETA_ESTADO[p.activo.operational_status]} color={COLOR_ESTADO[p.activo.operational_status]} />
          <Etiqueta texto={ETIQUETA_APTITUD[p.activo.safety_status]} color={COLOR_APTITUD[p.activo.safety_status]} />
          {p.activo.archived_at && <Etiqueta texto="Archivado" color="#94a3b8" />}
        </div>
      )}
      {pestanas.length > 1 && (
        <div className="pe2-vistas" role="tablist" aria-label="Secciones del activo">
          {pestanas.map(([k, t]) => (
            <button key={k} type="button" role="tab" aria-selected={pestana === k} className={pestana === k ? 'on' : ''} onClick={() => setPestana(k)}>{t}</button>
          ))}
        </div>
      )}
      {pestana === 'ficha' && <Ficha {...p} />}
      {pestana === 'estado' && p.activo && <EstadoAptitud {...p} activo={p.activo} />}
      {pestana === 'documentos' && p.activo && <Documentos {...p} activo={p.activo} />}
      {pestana === 'historial' && p.activo && <Historial activo={p.activo} />}
      {pestana === 'inspecciones' && p.activo && <Inspecciones {...p} activo={p.activo} />}
      {pestana === 'qr' && p.activo && <Qr activo={p.activo} empresa={p.empresa} />}
    </div>
  );
}

// ── Ficha ──
function Ficha(p: Props) {
  const matriz = p.sedes.find(s => s.is_main && s.active) ?? p.sedes.find(s => s.active) ?? null;
  const inicial = useMemo<CambiosActivo>(() => (p.activo ? { ...p.activo } : vacio(matriz?.id ?? null)), [p.activo, matriz?.id]);
  const [d, setD] = useState<CambiosActivo>(inicial);
  useEffect(() => { setD(inicial); }, [inicial]);
  const [guardado, setGuardado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  const [confirmar, setConfirmar] = useState<'archivar' | 'borrar' | null>(null);
  const [nuevoPeligro, setNuevoPeligro] = useState('');
  const ro = !p.puedeEditar || !!p.activo?.archived_at;
  const cat = (d.category ?? 'maquina') as Categoria;
  const def = CATEGORIAS[cat];
  const set = (c: CambiosActivo) => { setD(x => ({ ...x, ...c })); setGuardado(null); };
  const setTech = (k: string, v: string | number | null) => set({ tech: { ...(d.tech ?? {}), [k]: v === '' ? null : v } });
  const sugerido = siguienteCodigo(cat, p.codigos);
  const cambios = p.activo ? Object.fromEntries(Object.entries(d).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify((p.activo as unknown as Record<string, unknown>)[k]))) : d;
  const hayCambios = Object.keys(cambios).length > 0;
  const tech = (d.tech ?? {}) as FilaActivo['tech'];
  const venc = vencimientos({ category: cat, tech, manufacture_year: d.manufacture_year ?? null }, p.hoy, p.plazos);
  // Recarga nueva o cambiada: la base la rechaza con la prueba hidrostática vencida o en un no recargable; aquí se avisa antes.
  const recargaNueva = !!tech.ultima_recarga && tech.ultima_recarga !== (p.activo?.tech ?? {}).ultima_recarga;
  const avisoRecarga = recargaNueva ? bloqueoRecarga({ category: cat, tech, manufacture_year: d.manufacture_year ?? null }, p.plazos) : null;

  const validar = (): string | null => {
    if (!(d.name ?? '').trim()) return 'Escribe el nombre del activo.';
    if (d.code && d.code.trim() && codigoRepetido(d.code, p.codigos, p.activo?.code)) return 'Ese código ya lo usa otro activo de la empresa.';
    if (d.manufacture_year && (d.manufacture_year < 1900 || d.manufacture_year > 2100)) return 'El año de fabricación no es válido.';
    if ((d.isolation_points ?? []).some(x => !x.tipo)) return 'Cada punto de aislamiento necesita el tipo de energía.';
    if (avisoRecarga) return avisoRecarga;
    return null;
  };
  const guardar = async () => {
    const err = validar();
    if (err) { setGuardado('error'); setDetalle(err); return; }
    setGuardado('guardando'); setDetalle('');
    try {
      const limpio = { ...cambios } as CambiosActivo;
      delete (limpio as Record<string, unknown>).archived_at;
      if (p.activo) { delete limpio.operational_status; delete limpio.safety_status; }
      const a = p.activo ? await actualizarActivo(p.activo.id, limpio) : await crearActivo(p.companyId, { ...limpio, code: (d.code ?? '').trim() });
      setGuardado('guardado');
      p.onGuardado(a);
    } catch (e) { setGuardado('error'); setDetalle(msg(e)); }
  };
  const archivar = async (restaurar: boolean) => {
    if (!p.activo) return;
    setGuardado('guardando');
    try { p.onGuardado(await actualizarActivo(p.activo.id, { archived_at: restaurar ? null : new Date().toISOString() })); setGuardado('guardado'); setConfirmar(null); }
    catch (e) { setGuardado('error'); setDetalle(msg(e)); }
  };
  const borrar = async () => {
    if (!p.activo) return;
    try { await borrarActivo(p.activo.id); p.onBorrado(p.activo.id); } catch (e) { setGuardado('error'); setDetalle(msg(e)); setConfirmar(null); }
  };

  const texto = (k: keyof CambiosActivo, max = 160) => (
    <input type="text" maxLength={max} disabled={ro} value={(d[k] as string | null) ?? ''} onChange={e => set({ [k]: e.target.value || null } as CambiosActivo)} />
  );
  const fecha = (k: 'acquired_on' | 'installed_on') => (
    <input type="date" disabled={ro} value={d[k] ?? ''} onChange={e => set({ [k]: e.target.value || null })} />
  );

  return (
    <div className="mant-form">
      {p.activo?.archived_at && <div className="pe2-aviso">Activo archivado: es de solo lectura. Restáuralo para editarlo.</div>}
      <section className="pe2-card">
        <h3>Identificación</h3>
        <div className="pe2-grid">
          <Campo etiqueta="Nombre *">{texto('name')}</Campo>
          <Campo etiqueta="Categoría *">
            <select disabled={ro} value={cat} onChange={e => set({ category: e.target.value as Categoria })}>
              {LISTA_CATEGORIAS.map(c => <option key={c} value={c}>{CATEGORIAS[c].etiqueta}{CATEGORIAS[c].emergencia ? ' (emergencia)' : ''}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Código" nota={p.activo ? (p.esAdmin ? 'Solo el administrador lo cambia.' : 'Lo cambia el administrador.') : `Déjalo vacío y se asigna solo (${sugerido}).`}>
            <input type="text" maxLength={40} disabled={ro || (!!p.activo && !p.esAdmin)} placeholder={p.activo ? undefined : sugerido}
              value={d.code ?? ''} onChange={e => set({ code: e.target.value.toUpperCase() })} />
          </Campo>
          <Campo etiqueta="Fabricante">{texto('manufacturer', 120)}</Campo>
          <Campo etiqueta="Marca">{texto('brand', 120)}</Campo>
          <Campo etiqueta="Modelo">{texto('model', 120)}</Campo>
          <Campo etiqueta="Número de serie">{texto('serial_number', 120)}</Campo>
          <Campo etiqueta="Año de fabricación">
            <input type="number" inputMode="numeric" min={1900} max={2100} disabled={ro} value={d.manufacture_year ?? ''}
              onChange={e => set({ manufacture_year: e.target.value ? Number(e.target.value) : null })} />
          </Campo>
          <Campo etiqueta="Criticidad">
            <select disabled={ro} value={d.criticality ?? 'media'} onChange={e => set({ criticality: e.target.value as CambiosActivo['criticality'] })}>
              {CRITICIDADES.map(c => <option key={c} value={c}>{ETIQUETA_CRITICIDAD[c]}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Descripción" ancho>
            <textarea rows={2} maxLength={2000} disabled={ro} value={d.description ?? ''} onChange={e => set({ description: e.target.value || null })} />
          </Campo>
        </div>
        {p.activo && <Foto {...p} activo={p.activo} ro={ro} />}
      </section>

      {!p.activo && (
        <section className="pe2-card">
          <h3>Estado al registrarlo</h3>
          <div className="pe2-grid">
            <Campo etiqueta="Estado operativo">
              <select value={d.operational_status ?? 'operativo'} onChange={e => set({ operational_status: e.target.value as EstadoOperativo })}>
                {ESTADOS.map(s => <option key={s} value={s}>{ETIQUETA_ESTADO[s]}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Aptitud de seguridad" nota="Después se cambia de a uno, con el motivo.">
              <select value={d.safety_status ?? 'apto'} onChange={e => set({ safety_status: e.target.value as Aptitud })}>
                {APTITUDES.map(s => <option key={s} value={s}>{ETIQUETA_APTITUD[s]}</option>)}
              </select>
            </Campo>
          </div>
        </section>
      )}

      <section className="pe2-card">
        <h3>Ubicación</h3>
        <div className="pe2-grid">
          {p.varias && (
            <Campo etiqueta="Sede">
              <select disabled={ro} value={d.site_id ?? ''} onChange={e => set({ site_id: e.target.value || null })}>
                <option value="">Sin sede</option>
                {p.sedes.filter(s => s.active || s.id === d.site_id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Campo>
          )}
          <Campo etiqueta="Área">{texto('area')}</Campo>
          <Campo etiqueta="Ubicación específica">{texto('location_detail', 240)}</Campo>
          {p.opciones.sectores.length > 0 && (
            <Campo etiqueta="Sector del plan de emergencias" nota="Opcional: une el equipo con el sector del plan.">
              <select disabled={ro} value={d.emergency_sector_id ?? ''} onChange={e => set({ emergency_sector_id: e.target.value || null })}>
                <option value="">Sin vínculo</option>
                {p.opciones.sectores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </Campo>
          )}
        </div>
      </section>

      <section className="pe2-card">
        <h3>Datos técnicos · {def.etiqueta}</h3>
        {def.campos.length === 0 && <p className="pe2-nota">Esta categoría no tiene campos técnicos propios. Usa la descripción si hace falta.</p>}
        <div className="pe2-grid">
          {def.campos.map(c => {
            const v = (d.tech ?? {})[c.clave];
            return (
              <Campo key={c.clave} etiqueta={c.unidad ? `${c.etiqueta} (${c.unidad})` : c.etiqueta} nota={c.ayuda}>
                {c.tipo === 'opcion' ? (
                  <select disabled={ro} value={(v as string) ?? ''} onChange={e => setTech(c.clave, e.target.value)}>
                    <option value="">Sin dato</option>
                    {c.opciones?.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
                  </select>
                ) : c.tipo === 'fecha' ? (
                  <input type="date" disabled={ro} value={(v as string) ?? ''} onChange={e => setTech(c.clave, e.target.value)} />
                ) : c.tipo === 'numero' ? (
                  <input type="number" inputMode="decimal" min={0} step="any" disabled={ro} value={v ?? ''}
                    onChange={e => setTech(c.clave, e.target.value === '' ? null : Number(e.target.value))} />
                ) : (
                  <input type="text" maxLength={120} disabled={ro} value={(v as string) ?? ''} onChange={e => setTech(c.clave, e.target.value)} />
                )}
              </Campo>
            );
          })}
        </div>
        {venc.length > 0 && (
          <div className="mant-venc" aria-label="Vencimientos">
            {venc.map(v => (
              <div key={v.clave}>
                <Etiqueta texto={ETIQUETA_VENCIMIENTO[v.estado]} color={colorVence[v.estado]} />
                <span>{v.etiqueta}: <b>{fechaEc(v.fecha)}</b>{v.calculado ? ' (calculada)' : ''}</span>
              </div>
            ))}
            <small className="pe2-nota">{NOTA_VENCIMIENTOS}</small>
          </div>
        )}
        {avisoRecarga && <p className="tab-error" role="alert">{avisoRecarga}</p>}
        <div className="pe2-grid">
          <Campo etiqueta="Fecha de adquisición">{fecha('acquired_on')}</Campo>
          <Campo etiqueta="Fecha de instalación">{fecha('installed_on')}</Campo>
          <Campo etiqueta="Vida útil estimada (años)">
            <input type="number" inputMode="decimal" min={0} max={100} step="any" disabled={ro} value={d.useful_life_years ?? ''}
              onChange={e => set({ useful_life_years: e.target.value ? Number(e.target.value) : null })} />
          </Campo>
          <Campo etiqueta="Manual" nota="Dónde está o un enlace; el archivo se sube en Documentos.">{texto('manual_ref', 500)}</Campo>
        </div>
      </section>

      <section className="pe2-card">
        <h3>Responsables</h3>
        <div className="pe2-grid">
          <Campo etiqueta="Trabajador responsable" nota={p.opciones.trabajadores.length ? undefined : 'Registra trabajadores en el módulo Trabajadores.'}>
            <select disabled={ro} value={d.responsible_worker_id ?? ''} onChange={e => set({ responsible_worker_id: e.target.value || null })}>
              <option value="">Sin asignar</option>
              {p.opciones.trabajadores.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Usuario responsable">
            <select disabled={ro} value={d.responsible_user_id ?? ''} onChange={e => set({ responsible_user_id: e.target.value || null })}>
              <option value="">Sin asignar</option>
              {p.opciones.usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Proveedor externo">{texto('external_provider', 200)}</Campo>
        </div>
      </section>

      <section className="pe2-card">
        <h3>Seguridad</h3>
        <fieldset className="mant-fieldset" disabled={ro}>
          <legend>Peligros asociados</legend>
          <div className="tab-chips">
            {Array.from(new Set([...PELIGROS_SUGERIDOS, ...(d.hazards ?? [])])).map(h => {
              const on = (d.hazards ?? []).includes(h);
              return (
                <button key={h} type="button" className={`tab-chip${on ? ' on' : ''}`} aria-pressed={on}
                  onClick={() => set({ hazards: on ? (d.hazards ?? []).filter(x => x !== h) : [...(d.hazards ?? []), h] })}>{h}</button>
              );
            })}
          </div>
          {!ro && (
            <div className="mant-fila-mini">
              <input type="text" maxLength={60} placeholder="Otro peligro" aria-label="Otro peligro" value={nuevoPeligro} onChange={e => setNuevoPeligro(e.target.value)} />
              <button type="button" className="pe2-btn" disabled={!nuevoPeligro.trim()}
                onClick={() => { set({ hazards: Array.from(new Set([...(d.hazards ?? []), nuevoPeligro.trim()])) }); setNuevoPeligro(''); }}>Agregar</button>
            </div>
          )}
        </fieldset>
        <fieldset className="mant-fieldset" disabled={ro}>
          <legend>EPP requerido</legend>
          {p.opciones.epp.length === 0 ? <p className="pe2-nota">Aún no hay EPP en el catálogo. Regístralo en el módulo EPP y vuelve a elegirlo aquí.</p> : (
            <div className="tab-chips">
              {p.opciones.epp.map(e => {
                const on = (d.required_epp_ids ?? []).includes(e.id);
                return (
                  <button key={e.id} type="button" className={`tab-chip${on ? ' on' : ''}`} aria-pressed={on}
                    onClick={() => set({ required_epp_ids: on ? (d.required_epp_ids ?? []).filter(x => x !== e.id) : [...(d.required_epp_ids ?? []), e.id] })}>{e.nombre}</button>
                );
              })}
            </div>
          )}
        </fieldset>
        <fieldset className="mant-fieldset" disabled={ro}>
          <legend>Puntos de aislamiento de energía</legend>
          {(d.isolation_points ?? []).length === 0 && <p className="pe2-nota">Sin puntos registrados (por ejemplo: eléctrica, en el tablero TD-2, breaker 4).</p>}
          {(d.isolation_points ?? []).map((pt, i) => (
            <div key={i} className="mant-fila-mini">
              <select aria-label={`Tipo de energía del punto ${i + 1}`} value={pt.tipo} onChange={e => {
                const l = [...(d.isolation_points ?? [])]; l[i] = { ...pt, tipo: e.target.value }; set({ isolation_points: l });
              }}>
                <option value="">Tipo de energía</option>
                {TIPOS_ENERGIA.map(t => <option key={t.valor} value={t.valor}>{t.etiqueta}</option>)}
              </select>
              <input type="text" maxLength={240} placeholder="Ubicación del punto" aria-label={`Ubicación del punto ${i + 1}`} value={pt.ubicacion ?? ''}
                onChange={e => { const l = [...(d.isolation_points ?? [])]; l[i] = { ...pt, ubicacion: e.target.value }; set({ isolation_points: l }); }} />
              {!ro && <button type="button" className="pe2-btn peligro" aria-label={`Quitar el punto ${i + 1}`}
                onClick={() => set({ isolation_points: (d.isolation_points ?? []).filter((_, j) => j !== i) })}>Quitar</button>}
            </div>
          ))}
          {!ro && <button type="button" className="pe2-btn" onClick={() => set({ isolation_points: [...(d.isolation_points ?? []), { tipo: 'electrica', ubicacion: '' }] })}>
            Agregar punto de aislamiento</button>}
        </fieldset>
        <div className="pe2-grid">
          <Campo etiqueta="Permiso de trabajo asociado">
            <select disabled={ro} value={d.permit_type ?? ''} onChange={e => set({ permit_type: (e.target.value || null) as CambiosActivo['permit_type'] })}>
              <option value="">Ninguno</option>
              {TIPOS_PERMISO.map(t => <option key={t} value={t}>{ETIQUETA_PERMISO[t]}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Procedimiento asociado">{texto('procedure_ref', 500)}</Campo>
          <Campo etiqueta="Restricciones de operación" ancho>
            <textarea rows={2} maxLength={2000} disabled={ro} value={d.operating_restrictions ?? ''} onChange={e => set({ operating_restrictions: e.target.value || null })} />
          </Campo>
        </div>
      </section>

      {p.puedeEditar && (
        <div className="pe2-barra">
          <GuardadoVisible estado={guardado} detalle={detalle} />
          <div className="pe2-acciones">
            {p.activo && !p.activo.archived_at && <button type="button" className="pe2-btn peligro" onClick={() => setConfirmar('archivar')}>Archivar</button>}
            {p.activo?.archived_at && <button type="button" className="pe2-btn" onClick={() => archivar(true)}>Restaurar</button>}
            {p.activo && <button type="button" className="pe2-btn peligro" onClick={() => setConfirmar('borrar')}>Borrar</button>}
            {!ro && <button type="button" className="pe2-btn primario" disabled={!hayCambios || guardado === 'guardando'} onClick={guardar}>
              {p.activo ? 'Guardar cambios' : 'Registrar activo'}</button>}
          </div>
        </div>
      )}
      {confirmar && (
        <div className="tab-confirmar mant-confirmar" role="alertdialog" aria-label="Confirmar">
          <p>{confirmar === 'archivar'
            ? `¿Archivar ${p.activo?.code}? Queda de solo lectura y fuera del tablero; su historial se conserva y puedes restaurarlo.`
            : `¿Borrar ${p.activo?.code}? Solo se puede si no tiene historial (inspecciones, documentos o cambios de estado); si lo tiene, archívalo.`}</p>
          <button type="button" className="pe2-btn peligro" onClick={() => (confirmar === 'archivar' ? archivar(false) : borrar())}>
            {confirmar === 'archivar' ? 'Sí, archivar' : 'Sí, borrar'}</button>
          <button type="button" className="pe2-btn" onClick={() => setConfirmar(null)}>Cancelar</button>
        </div>
      )}
    </div>
  );
}

function Foto(p: Props & { activo: FilaActivo; ro: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [estado, setEstado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  useEffect(() => {
    let vivo = true;
    setUrl(null);
    if (p.activo.photo_path) urlFirmada(p.activo.photo_path).then(u => { if (vivo) setUrl(u); }).catch(() => {});
    return () => { vivo = false; };
  }, [p.activo.photo_path]);
  return (
    <div className="mant-foto">
      {url ? <img src={url} alt={`Foto de ${p.activo.name}`} /> : <span className="pe2-nota">{p.activo.photo_path ? 'Cargando la foto…' : 'Sin foto.'}</span>}
      {!p.ro && (
        <label className="pe2-btn mant-subir">
          {p.activo.photo_path ? 'Cambiar la foto' : 'Subir una foto'}
          <input type="file" accept="image/png,image/jpeg" className="pe2-sr" onChange={async e => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return;
            setEstado('guardando');
            try { p.onGuardado(await subirFoto(p.companyId, p.activo.id, f)); setEstado('guardado'); } catch (er) { setEstado('error'); setDetalle(msg(er)); }
          }} />
        </label>
      )}
      <GuardadoVisible estado={estado} detalle={detalle} />
    </div>
  );
}

// ── Estado y aptitud ──
function EstadoAptitud(p: Props & { activo: FilaActivo }) {
  const a = p.activo;
  const antes = { estado: a.operational_status, aptitud: a.safety_status };
  const [estado, setEstado] = useState<EstadoOperativo>(a.operational_status);
  const [aptitud, setAptitud] = useState<Aptitud>(a.safety_status);
  const [motivo, setMotivo] = useState('');
  const [guardado, setGuardado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  useEffect(() => { setEstado(a.operational_status); setAptitud(a.safety_status); setMotivo(''); }, [a.operational_status, a.safety_status]);
  const pedido = { estado, aptitud };
  const despues = resultadoCambio(antes, pedido);
  const cambia = despues.estado !== antes.estado || despues.aptitud !== antes.aptitud;
  const error = cambia ? validarCambio(antes, pedido, motivo) : null;
  const aviso = cambia ? avisoCambio(antes, pedido) : null;
  const ro = !p.puedeEditar || !!a.archived_at;
  const guardar = async () => {
    if (error) { setGuardado('error'); setDetalle(error); return; }
    setGuardado('guardando'); setDetalle('');
    try {
      const cambio: CambiosActivo = {};
      if (estado !== a.operational_status) cambio.operational_status = estado;
      if (despues.aptitud !== a.safety_status) cambio.safety_status = despues.aptitud;
      p.onGuardado(await cambiarEstado(a.id, cambio, motivo));
      setGuardado('guardado'); setMotivo('');
    } catch (e) { setGuardado('error'); setDetalle(msg(e)); }
  };
  return (
    <section className="pe2-card">
      <h3>Estado operativo y aptitud de seguridad</h3>
      <p className="pe2-sub">Son dos datos separados: un equipo puede estar operativo y no apto hasta que alguien lo verifique. Se cambian de a un activo
        y cada cambio queda en el historial con su motivo.</p>
      <div className="pe2-grid">
        <Campo etiqueta="Estado operativo">
          <select disabled={ro} value={estado} onChange={e => setEstado(e.target.value as EstadoOperativo)}>
            {ESTADOS.map(s => <option key={s} value={s}>{ETIQUETA_ESTADO[s]}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Aptitud de seguridad">
          <select disabled={ro} value={despues.aptitud} onChange={e => setAptitud(e.target.value as Aptitud)}>
            {APTITUDES.map(s => <option key={s} value={s}>{ETIQUETA_APTITUD[s]}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Motivo" ancho nota="Obligatorio para liberar el equipo (de fuera de servicio a operativo, o de no apto a apto): qué se verificó y quién.">
          <textarea rows={3} maxLength={1000} disabled={ro} value={motivo} onChange={e => setMotivo(e.target.value)} />
        </Campo>
      </div>
      {aviso && <div className="pe2-aviso">{aviso}</div>}
      {cambia && error && <p className="tab-error" role="alert">{error}</p>}
      {!ro && (
        <div className="pe2-acciones">
          <GuardadoVisible estado={guardado} detalle={detalle} />
          <button type="button" className="pe2-btn primario" disabled={!cambia || !!error || guardado === 'guardando'} onClick={guardar}>Guardar el cambio</button>
        </div>
      )}
    </section>
  );
}

// ── Documentos ──
function Documentos(p: Props & { activo: FilaActivo }) {
  const [docs, setDocs] = useState<FilaDocumento[]>([]);
  const [error, setError] = useState('');
  const [kind, setKind] = useState<TipoDocumento>('manual');
  const [titulo, setTitulo] = useState('');
  const [vence, setVence] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [estado, setEstado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  const [aBorrar, setABorrar] = useState<string | null>(null);
  const hoyIso = isoLocal(p.hoy);
  const recargar = useCallback(async () => { try { setDocs(await cargarDocumentos(p.activo.id)); setError(''); } catch (e) { setError(msg(e)); } }, [p.activo.id]);
  useEffect(() => { recargar(); }, [recargar]);
  const ro = !p.puedeEditar || !!p.activo.archived_at;
  const subir = async () => {
    if (!archivo) return;
    const err = validarArchivo(archivo);
    if (err) { setEstado('error'); setDetalle(err); return; }
    setEstado('guardando');
    try { await subirDocumento(p.companyId, p.activo.id, kind, archivo, titulo, vence || null); setArchivo(null); setTitulo(''); setVence(''); setEstado('guardado'); await recargar(); }
    catch (e) { setEstado('error'); setDetalle(msg(e)); }
  };
  return (
    <section className="pe2-card">
      <h3>Documentos del activo</h3>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {docs.length === 0 && <p className="pe2-nota">Aún no hay documentos. Sube el manual, la ficha técnica, certificados, garantías, informes o fotos (PDF, PNG o JPEG, hasta 20 MB).</p>}
      <ul className="mant-lista">
        {docs.map(d => (
          <li key={d.id}>
            <div>
              <b>{ETIQUETA_DOCUMENTO[d.kind]}</b> · {d.title || d.file_name}
              {d.valid_until && <> · <Etiqueta texto={d.valid_until < hoyIso ? 'Vencido' : 'Vigente'} color={d.valid_until < hoyIso ? '#ef4444' : '#22c55e'} /> hasta {fechaEc(d.valid_until)}</>}
              <small className="pe2-nota"> · subido el {fechaEc(d.created_at)}</small>
            </div>
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" onClick={() => abrirArchivoActivo(d.file_path).catch(e => setError(msg(e)))}>Abrir</button>
              {!ro && (aBorrar === d.id ? (
                <>
                  <button type="button" className="pe2-btn peligro" onClick={async () => { try { await borrarDocumento(d.id); setABorrar(null); await recargar(); } catch (e) { setError(msg(e)); } }}>Sí, borrar</button>
                  <button type="button" className="pe2-btn" onClick={() => setABorrar(null)}>Cancelar</button>
                </>
              ) : <button type="button" className="pe2-btn peligro" onClick={() => setABorrar(d.id)}>Borrar</button>)}
            </div>
          </li>
        ))}
      </ul>
      {!ro && (
        <div className="pe2-grid">
          <Campo etiqueta="Tipo">
            <select value={kind} onChange={e => setKind(e.target.value as TipoDocumento)}>
              {TIPOS_DOCUMENTO.map(t => <option key={t} value={t}>{ETIQUETA_DOCUMENTO[t]}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Título (opcional)"><input type="text" maxLength={200} value={titulo} onChange={e => setTitulo(e.target.value)} /></Campo>
          <Campo etiqueta="Vigente hasta (opcional)" nota="Para certificados y garantías."><input type="date" value={vence} onChange={e => setVence(e.target.value)} /></Campo>
          <Campo etiqueta="Archivo">
            <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={e => setArchivo(e.target.files?.[0] ?? null)} />
          </Campo>
          <div className="pe2-acciones mant-ancho">
            <GuardadoVisible estado={estado} detalle={detalle} />
            <button type="button" className="pe2-btn primario" disabled={!archivo || estado === 'guardando'} onClick={subir}>Subir documento</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Historial ──
function Historial({ activo }: { activo: FilaActivo }) {
  const [filas, setFilas] = useState<FilaHistorial[]>([]);
  const [error, setError] = useState('');
  useEffect(() => { cargarHistorial(activo.id).then(setFilas).catch(e => setError(msg(e))); }, [activo.id, activo.updated_at]);
  const valor = (ev: string, v: string | null) => {
    if (!v) return '—';
    if (ev === 'estado') return ETIQUETA_ESTADO[v as EstadoOperativo] ?? v;
    if (ev === 'aptitud') return ETIQUETA_APTITUD[v as Aptitud] ?? v;
    if (ev === 'inspeccion') return ETIQUETA_ESTADO_INSPECCION[v as EstadoInspeccion] ?? v;
    if (ev.startsWith('documento')) return ETIQUETA_DOCUMENTO[v as TipoDocumento] ?? v;
    return v;
  };
  return (
    <section className="pe2-card">
      <h3>Historial</h3>
      <p className="pe2-sub">Registro automático de lo que pasó con el activo. No se edita ni se borra.</p>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {filas.length === 0 && !error && <p className="pe2-nota">Sin movimientos.</p>}
      <ol className="mant-historial">
        {filas.map(h => {
          const det = h.detail ?? {};
          const campos = Array.isArray(det.campos) ? (det.campos as string[]).map(c => ETIQUETA_CAMPO[c] ?? c) : [];
          return (
            <li key={h.id}>
              <div className="mant-hist-cabeza">
                <b>{ETIQUETA_EVENTO[h.event] ?? h.event}</b>
                <small>{new Date(h.created_at).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })} · {h.actor_name || 'Sistema'}</small>
              </div>
              {(h.event === 'estado' || h.event === 'aptitud' || h.event === 'codigo') && <span>{valor(h.event, h.from_value)} → {valor(h.event, h.to_value)}</span>}
              {h.event === 'creado' && <span>Código {h.to_value}</span>}
              {(h.event === 'documento' || h.event === 'documento_eliminado') && <span>{valor(h.event, h.to_value)}: {String(det.titulo || det.nombre || '')}</span>}
              {h.event === 'inspeccion' && <span>{String(det.plantilla ?? '')} · {valor(h.event, h.to_value)}{det.fecha ? ` · ${fechaEc(String(det.fecha))}` : ''}</span>}
              {campos.length > 0 && <span>{campos.join(', ')}</span>}
              {det.automatico === true && <span className="pe2-nota">Automático: al entrar a mantenimiento o fuera de servicio queda no apto hasta verificarlo.</span>}
              {h.reason && <span className="mant-motivo">Motivo: {h.reason}</span>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ── Inspecciones del activo ──
function Inspecciones(p: Props & { activo: FilaActivo }) {
  const [lista, setLista] = useState<InspeccionActivo[]>([]);
  const [plantillas, setPlantillas] = useState<PlantillaMin[]>([]);
  const [tpl, setTpl] = useState('');
  const [frecuencia, setFrecuencia] = useState('mensual');
  const [inicio, setInicio] = useState(isoLocal(p.hoy));
  const [estado, setEstado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  const recargar = useCallback(async () => setLista(await cargarInspeccionesActivo(p.activo.id)), [p.activo.id]);
  useEffect(() => { recargar(); cargarPlantillasActivo(p.companyId).then(setPlantillas); }, [recargar, p.companyId]);
  const ordenadas = useMemo(() => [...plantillas].sort((x, y) =>
    Number(y.asset_class === p.activo.category) - Number(x.asset_class === p.activo.category) || x.name.localeCompare(y.name, 'es')), [plantillas, p.activo.category]);
  useEffect(() => { if (!tpl && ordenadas[0]) setTpl(ordenadas[0].id); }, [ordenadas, tpl]);
  const sede = p.activo.site_id ?? p.sedes.find(s => s.is_main)?.id ?? null;
  const ro = !p.puedeEditar || !!p.activo.archived_at;
  const programar = async () => {
    if (!sede) { setEstado('error'); setDetalle('El activo necesita una sede para programar la inspección.'); return; }
    setEstado('guardando');
    try {
      await programarInspeccionActivo({ companyId: p.companyId, assetId: p.activo.id, siteId: sede, templateId: tpl, frecuencia, inicio, usuarioId: p.usuarioId });
      setEstado('guardado'); await recargar();
    } catch (e) { setEstado('error'); setDetalle(msg(e)); }
  };
  return (
    <section className="pe2-card">
      <h3>Inspecciones del activo</h3>
      <p className="pe2-sub">Se ejecutan y firman en Inspecciones; las firmadas cuentan para el Checklist (por ejemplo GT 10, GT 11, GT 27 y GT 28) y quedan en el historial.</p>
      {lista.length === 0 && <p className="pe2-nota">Aún no hay inspecciones de este activo.</p>}
      <ul className="mant-lista">
        {lista.map(i => (
          <li key={i.id}>
            <div><b>{i.template_name}</b> · {ETIQUETA_ESTADO_INSPECCION[i.status as EstadoInspeccion] ?? i.status} · {fechaEc(i.performed_date ?? i.scheduled_date)}</div>
          </li>
        ))}
      </ul>
      {!ro && (
        <div className="pe2-grid">
          <Campo etiqueta="Plantilla" nota="Primero las sugeridas para esta categoría.">
            <select value={tpl} onChange={e => setTpl(e.target.value)}>
              {ordenadas.map(t => <option key={t.id} value={t.id}>{t.name}{t.asset_class === p.activo.category ? ' (sugerida)' : ''}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Frecuencia">
            <select value={frecuencia} onChange={e => setFrecuencia(e.target.value)}>
              {FRECUENCIAS.map(f => <option key={f} value={f}>{ETIQUETA_FRECUENCIA[f]}</option>)}
            </select>
          </Campo>
          <Campo etiqueta="Primera fecha"><input type="date" value={inicio} onChange={e => setInicio(e.target.value)} /></Campo>
          <div className="pe2-acciones mant-ancho">
            <GuardadoVisible estado={estado} detalle={detalle} />
            <button type="button" className="pe2-btn primario" disabled={!tpl || estado === 'guardando'} onClick={programar}>Programar inspección</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── QR ──
function Qr({ activo, empresa }: { activo: FilaActivo; empresa: string }) {
  const enlace = enlaceActivo(window.location.origin, activo.id);
  const [img, setImg] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { qrDataUrl(enlace).then(setImg).catch(() => setError('No se pudo generar el QR.')); }, [enlace]);
  const imprimir = () => {
    if (!img) return;
    const v = window.open('', '_blank');
    if (!v) { setError('El navegador bloqueó la ventana de impresión. Permite ventanas emergentes e intenta de nuevo.'); return; }
    v.document.write(htmlEtiqueta({ codigo: activo.code, nombre: activo.name, empresa, qr: img }));
    v.document.close();
  };
  return (
    <section className="pe2-card mant-qr">
      <h3>QR del activo</h3>
      <p className="pe2-sub">El QR abre la ficha solo con la sesión iniciada y para usuarios de tu empresa. Sin sesión, lleva al inicio de sesión y no muestra ningún dato.</p>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {img ? <img src={img} alt={`Código QR del activo ${activo.code}`} width={220} height={220} /> : !error && <p className="pe2-nota">Generando…</p>}
      <p className="pe2-nota mant-enlace">{enlace}</p>
      <div className="pe2-acciones">
        <a className={`pe2-btn${img ? '' : ' mant-inactivo'}`} href={img ?? undefined} download={`QR_${activo.code}.png`} aria-disabled={!img}>Descargar imagen</a>
        <button type="button" className="pe2-btn primario" disabled={!img} onClick={imprimir}>Imprimir etiqueta</button>
      </div>
      <small className="pe2-nota">Etiqueta de 62 mm con el código, el nombre y el QR.</small>
    </section>
  );
}
